import React, { useState, useEffect, useCallback } from "react";
import {
  Database, RefreshCcw, Play, AlertTriangle,
  CheckCircle2, Clock, Users, GraduationCap, School, FileText, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import DemoAnalyticsPreview from "./DemoAnalyticsPreview";

const API = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-gray-200 p-3 flex items-center gap-3" data-testid={`demo-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className={`w-9 h-9 flex items-center justify-center rounded`} style={{ backgroundColor: `${color}15` }}>
      <Icon size={16} style={{ color }} />
    </div>
    <div>
      <div className="text-xl font-bold">{value ?? "..."}</div>
      <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

const DemoDataManager = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/demo-status`);
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch demo status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/seed-demo`, {});
      setSeedResult(res.data);
      await fetchStatus();
    } catch (err) {
      setSeedResult({ status: "error", message: err.response?.data?.detail || err.message });
    } finally {
      setSeeding(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setSeedResult(null);
    setConfirmReset(false);
    try {
      const res = await axios.post(`${API}/api/admin/reset-demo`, {});
      setSeedResult(res.data);
      await fetchStatus();
    } catch (err) {
      setSeedResult({ status: "error", message: err.response?.data?.detail || err.message });
    } finally {
      setResetting(false);
    }
  };

  const counts = status?.counts || {};
  const lastSeeded = status?.last_seeded;

  return (
    <div className="space-y-6" data-testid="demo-data-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 flex items-center justify-center">
            <Database size={20} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
              Demo Data Manager
            </h2>
            <p className="text-sm text-gray-500">Seed realistic student data for demos and sales presentations</p>
          </div>
        </div>
        {lastSeeded && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock size={12} />
            Last seeded: {new Date(lastSeeded).toLocaleDateString()} at{" "}
            {new Date(lastSeeded).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      {!loading && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          <StatCard icon={School} label="Districts" value={counts.districts} color="#3b82f6" />
          <StatCard icon={School} label="Schools" value={counts.schools} color="#6366f1" />
          <StatCard icon={GraduationCap} label="Teachers" value={counts.teachers} color="#8b5cf6" />
          <StatCard icon={Users} label="Students" value={counts.students} color="#22c55e" />
          <StatCard icon={FileText} label="Classes" value={counts.classes} color="#06b6d4" />
          <StatCard icon={FileText} label="Results" value={counts.results} color="#f59e0b" />
          <StatCard icon={FileText} label="Sessions" value={counts.sessions} color="#f97316" />
          <StatCard icon={FileText} label="Responses" value={counts.responses} color="#e11d48" />
          <StatCard icon={Target} label="Goals" value={counts.goals} color="#10b981" />
        </div>
      )}

      {/* Teacher Credentials Card */}
      <div className="bg-slate-50 border border-slate-200 p-4">
        <div className="text-xs font-bold text-slate-700 mb-3 uppercase tracking-wider">Demo Teacher Accounts (share with sales reps)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: "Ms. Rivera", school: "PS 124 Yung Wing Elementary", email: "demo.teacher@ps124.nyc", profile: "Strong", profileColor: "bg-green-100 text-green-700" },
            { name: "Ms. Chang", school: "PS 234 Independence School", email: "ms.chang@ps234.nyc", profile: "Average", profileColor: "bg-blue-100 text-blue-700" },
            { name: "Mr. Okafor", school: "MS 131 Dr. Sun Yat Sen", email: "mr.okafor@ms131.nyc", profile: "Needs Support", profileColor: "bg-red-100 text-red-700" },
            { name: "Ms. Garcia", school: "HS 525 West Side", email: "ms.garcia@hs525.nyc", profile: "Improving", profileColor: "bg-amber-100 text-amber-700" },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 p-3 flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">{t.name} — {t.school}</div>
                <div className="text-xs text-gray-500 font-mono">{t.email}</div>
              </div>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${t.profileColor}`}>{t.profile}</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-gray-400 mt-2">Password for all demo teachers: <span className="font-mono font-semibold">{["Demo","Teach","20","26","!"].join("")}</span></div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 items-center">
        <Button
          onClick={handleSeed}
          disabled={seeding || resetting}
          className="bg-[#002FA7] hover:bg-[#001f6f] text-white btn-sharp"
          data-testid="seed-demo-btn"
        >
          {seeding ? (
            <><RefreshCcw size={14} className="mr-1.5 animate-spin" /> Seeding (this takes ~30s)...</>
          ) : (
            <><Play size={14} className="mr-1.5" /> Seed Demo Data</>
          )}
        </Button>

        {!confirmReset ? (
          <Button
            variant="outline"
            onClick={() => setConfirmReset(true)}
            disabled={seeding || resetting}
            className="btn-sharp border-red-300 text-red-600 hover:bg-red-50"
            data-testid="reset-demo-btn"
          >
            <RefreshCcw size={14} className="mr-1.5" /> Reset & Re-Seed
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600 font-medium">This will clear ALL student, teacher, and class data. Continue?</span>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleReset}
              disabled={resetting}
              className="btn-sharp"
              data-testid="confirm-reset-btn"
            >
              {resetting ? (
                <><RefreshCcw size={12} className="mr-1 animate-spin" /> Resetting...</>
              ) : "Yes, Reset Everything"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmReset(false)}
              className="btn-sharp"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Result Banner */}
      {seedResult && (
        <div className={`p-4 border ${seedResult.status === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
          data-testid="seed-result-banner"
        >
          <div className="flex items-center gap-2 mb-1">
            {seedResult.status === "success" ? (
              <CheckCircle2 size={16} className="text-green-600" />
            ) : (
              <AlertTriangle size={16} className="text-red-600" />
            )}
            <span className={`font-bold text-sm ${seedResult.status === "success" ? "text-green-800" : "text-red-800"}`}>
              {seedResult.message}
            </span>
          </div>
          {seedResult.output && (
            <pre className="text-[10px] text-gray-600 mt-2 bg-white p-2 border border-gray-100 max-h-40 overflow-auto whitespace-pre-wrap font-mono">
              {seedResult.output}
            </pre>
          )}
          {seedResult.seed_output && (
            <pre className="text-[10px] text-gray-600 mt-2 bg-white p-2 border border-gray-100 max-h-40 overflow-auto whitespace-pre-wrap font-mono">
              {seedResult.seed_output}
            </pre>
          )}
          {seedResult.cleared && (
            <div className="text-xs text-gray-600 mt-2">
              Cleared: {Object.entries(seedResult.cleared).map(([k,v]) => `${k}: ${v}`).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Analytics Preview */}
      <DemoAnalyticsPreview />

      {/* Usage Instructions */}
      <div className="bg-blue-50 border border-blue-200 p-4">
        <div className="text-xs font-bold text-blue-800 mb-2 uppercase tracking-wider">How to Prepare for a Demo</div>
        <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
          <li>Click <strong>"Seed Demo Data"</strong> to populate 106+ students across 4 NYC schools with assessment histories, goals, and growth trajectories</li>
          <li>Share the appropriate teacher credentials above with your sales rep — they log in as that teacher</li>
          <li>The teacher dashboard will show fully populated Growth Tracking, Projections, Cohort Comparison, and Analytics data</li>
          <li>Before each new demo, use <strong>"Reset & Re-Seed"</strong> to clear everything and generate fresh randomized data</li>
          <li>For the Presentation Playbook with demo scripts and talk tracks, go to the <strong>Playbook tab</strong></li>
        </ol>
      </div>
    </div>
  );
};

export default DemoDataManager;
